import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { api, errorMessage } from '@/api/client';
import { Spinner } from '@/components/Spinner';
import { cleanDrawnSignature, cleanSignatureImage, type CleanedSignature } from './clean';

/**
 * Capture a signature, either drawn or uploaded.
 *
 * Both paths end in the same place: a cleaned, trimmed, transparent PNG, sent
 * to S3 through a presigned URL. The bytes never pass through the API.
 */
type Mode = 'draw' | 'upload';

interface Stored {
  hasSignature: boolean;
  url?: string;
  source?: string;
  updatedAt?: number;
}

export function SignaturePad({ onSaved }: { onSaved?: () => void }): ReactNode {
  const [mode, setMode] = useState<Mode>('draw');
  const [existing, setExisting] = useState<Stored | null>(null);
  const [preview, setPreview] = useState<CleanedSignature | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const hasStrokes = useRef(false);

  const refresh = useCallback(async () => {
    try {
      setExisting(await api.signature.mine<Stored>());
    } catch (err) {
      setError(errorMessage(err));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Object URLs are a real leak if you forget them, and this component makes a
  // new one every time someone redraws.
  useEffect(() => {
    if (!preview) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(preview.blob);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [preview]);

  /* ---------------------------------------------------------- drawing --- */

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || mode !== 'draw') return;

    // Match the backing store to the device pixel ratio, or the stroke is a
    // blurry mess on any modern screen.
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.round(rect.width * ratio);
    canvas.height = Math.round(rect.height * ratio);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
    hasStrokes.current = false;
  }, [mode]);

  const pointAt = (e: React.PointerEvent<HTMLCanvasElement>): [number, number] => {
    const rect = e.currentTarget.getBoundingClientRect();
    return [e.clientX - rect.left, e.clientY - rect.top];
  };

  const start = (e: React.PointerEvent<HTMLCanvasElement>): void => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = true;
    hasStrokes.current = true;
    const [x, y] = pointAt(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e: React.PointerEvent<HTMLCanvasElement>): void => {
    if (!drawing.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const [x, y] = pointAt(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const end = (): void => {
    drawing.current = false;
  };

  const clearPad = (): void => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    hasStrokes.current = false;
    setPreview(null);
    setNotice(null);
  };

  /* ---------------------------------------------------------- prepare --- */

  const prepareDrawn = useCallback(async (): Promise<void> => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!hasStrokes.current) {
      setError('Draw your signature first.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const cleaned = await cleanDrawnSignature(canvas);
      setPreview(cleaned);
      setNotice('Trimmed and ready. Save it, or clear and draw again.');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }, []);

  const prepareUpload = useCallback(async (file: File): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      const cleaned = await cleanSignatureImage(file);
      setPreview(cleaned);
      setNotice(
        cleaned.looksEmpty
          ? 'Almost no ink was found — check this is a photo of a signature on light paper.'
          : 'Background removed and trimmed. Save it, or choose a different image.',
      );
    } catch {
      setError('That image could not be read. Try a PNG or JPEG photo of your signature.');
    } finally {
      setBusy(false);
    }
  }, []);

  /* ------------------------------------------------------------- save --- */

  const save = useCallback(async (): Promise<void> => {
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      const { url } = await api.signature.uploadUrl<{ url: string }>({
        contentLength: preview.blob.size,
      });
      // Straight to S3 — the image never goes through the API.
      const put = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'image/png' },
        body: preview.blob,
      });
      if (!put.ok) throw new Error(`storage rejected the upload (${put.status})`);

      await api.signature.save<unknown>({
        source: mode === 'draw' ? 'drawn' : 'uploaded',
        width: preview.width,
        height: preview.height,
      });
      setPreview(null);
      setNotice('Signature saved.');
      clearPad();
      await refresh();
      onSaved?.();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }, [preview, mode, refresh, onSaved]);

  const removeSignature = useCallback(async (): Promise<void> => {
    setBusy(true);
    try {
      await api.signature.delete<unknown>();
      setNotice('Signature removed.');
      await refresh();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }, [refresh]);

  return (
    <div className="panel">
      <h3>My Signature</h3>
      <p className="hint">
        Used on the printed claim form beside your name. Drawing it here is the stronger option —
        an uploaded image could be any file, and that difference matters if a signature is ever
        questioned.
      </p>

      {error ? (
        <div className="form-error" role="alert">
          {error}
        </div>
      ) : null}
      {notice ? <div className="form-notice">{notice}</div> : null}

      {existing?.hasSignature && !preview ? (
        <div className="sig-current">
          <span className="sig-current-label">Current signature</span>
          <img src={existing.url} alt="Your saved signature" className="sig-image" />
          <button type="button" className="btn btn-danger btn-sm" onClick={() => void removeSignature()} disabled={busy}>
            Remove
          </button>
        </div>
      ) : null}

      <div className="mode-switch" role="tablist" aria-label="How to provide a signature">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'draw'}
          className={mode === 'draw' ? 'mode-tab active' : 'mode-tab'}
          onClick={() => {
            setMode('draw');
            setPreview(null);
          }}
        >
          Draw it
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'upload'}
          className={mode === 'upload' ? 'mode-tab active' : 'mode-tab'}
          onClick={() => {
            setMode('upload');
            setPreview(null);
          }}
        >
          Upload a photo
        </button>
      </div>

      {mode === 'draw' ? (
        <>
          <canvas
            ref={canvasRef}
            className="sig-pad"
            onPointerDown={start}
            onPointerMove={move}
            onPointerUp={end}
            onPointerLeave={end}
            // Without this a touch drag scrolls the page instead of drawing.
            style={{ touchAction: 'none' }}
          />
          <p className="hint">Sign with a mouse, trackpad, stylus or finger.</p>
          <div className="row-actions">
            <button type="button" className="btn btn-ghost btn-sm" onClick={clearPad} disabled={busy}>
              Clear
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => void prepareDrawn()} disabled={busy}>
              {busy ? <Spinner /> : null} Preview
            </button>
          </div>
        </>
      ) : (
        <div className="field">
          <label htmlFor="sig-file">Photo or scan of your signature</label>
          <input
            id="sig-file"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void prepareUpload(file);
            }}
          />
          <p className="hint">
            Sign on white paper and photograph it straight on. The background is removed and the
            image trimmed here in your browser — nothing is uploaded until you save.
          </p>
        </div>
      )}

      {preview && previewUrl ? (
        <div className="sig-preview">
          <span className="sig-current-label">Preview — this is what will be printed</span>
          <img src={previewUrl} alt="Cleaned signature preview" className="sig-image" />
          <div className="row-actions">
            <button type="button" className="btn btn-good btn-sm" onClick={() => void save()} disabled={busy}>
              {busy ? <Spinner /> : null} Save signature
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPreview(null)} disabled={busy}>
              Discard
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
